import { type BriefContent, renderRichText } from '@sigora/domain';
import { useEffect, useId, useRef, useState } from 'react';

import {
  BRIEF_HEADINGS,
  EDITED_BY_YOU,
  HIDE_LABEL,
  SHOW_LABEL,
  HIDDEN_BULLET_PREFIX,
} from '../brief-copy.ts';
import { type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';
import { briefEditRefusal } from './brief-edit.ts';
import type { BriefEdits } from './Verdict.tsx';

/**
 * The Executive summary, one point per line in the order the Brief holds them. A bullet the
 * researcher hid stays on their page, struck through and read out after "Hidden:", so it can be
 * shown again; `edited` marks the block while any bullet is hidden, and `marks` a summary a Sigora
 * revision changed. With `edits`, each bullet can be hidden or shown.
 */
export const SummaryList = ({
  summary,
  edited,
  edits,
  marks,
}: {
  summary: BriefContent['summary'];
  edited: boolean;
  edits?: BriefEdits;
  marks?: RevisionMarks | null;
}) => {
  const idPrefix = useId();
  const [error, setError] = useState<string | null>(null);
  const toggles = useRef(new Map<string, HTMLButtonElement>());
  /** The bullet whose Hide or Show was just answered, which takes the focus back. */
  const [answeredId, setAnsweredId] = useState<string | null>(null);

  useEffect(() => {
    if (answeredId !== null) {
      toggles.current.get(answeredId)?.focus();
      setAnsweredId(null);
    }
  }, [answeredId]);

  const setVisible = async (itemId: string, visible: boolean) => {
    if (edits === undefined || edits.busy) {
      return;
    }

    setError(null);
    const outcome = await edits.send({ op: 'set_summary_visibility', item_id: itemId, visible });
    if (outcome.status === 'refused') {
      setError(briefEditRefusal(outcome.failure));
    }
    setAnsweredId(itemId);
  };

  return (
    <>
      <h2>
        {BRIEF_HEADINGS.summary}
        {edited && (
          <>
            {' '}
            <span className="brief-tag" data-test-id="brief-summary-edited">
              {EDITED_BY_YOU}
            </span>
          </>
        )}
        <UpdatedTag marks={marks} block="summary" />
      </h2>
      <ul className="brief-list">
        {summary.map((item) => {
          const text = renderRichText(item.text);
          const textId = `${idPrefix}-${item.id}`;

          return (
            <li
              key={item.id}
              className={item.visible ? undefined : 'brief-summary-item--hidden'}
              data-test-id="brief-summary-item"
              data-visible={String(item.visible)}
            >
              {!item.visible && (
                <span className="visually-hidden" data-test-id="brief-summary-hidden-prefix">
                  {HIDDEN_BULLET_PREFIX}
                </span>
              )}
              <span className="brief-summary-text" id={textId} data-test-id="brief-summary-text">
                {text}
              </span>
              {edits !== undefined && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="text-link brief-inline-action"
                    data-test-id="brief-summary-toggle"
                    aria-describedby={textId}
                    disabled={edits.busy}
                    ref={(button) => {
                      if (button === null) {
                        toggles.current.delete(item.id);
                      } else {
                        toggles.current.set(item.id, button);
                      }
                    }}
                    onClick={() => void setVisible(item.id, !item.visible)}
                  >
                    {item.visible ? HIDE_LABEL : SHOW_LABEL}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {error !== null && (
        <p className="chat-error" data-test-id="brief-summary-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
};
